import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { Formik } from 'formik';

import Col from 'components/ui/Col';
import Row from 'components/ui/Row';

import { Subcontent, DragAndDropSubcontent, Modal } from '@yuhuinc/ui-library/';

import { LeaseCreatorPageFooter } from '../common/LeaseCreatorPageFooter';
import { StyledContainer } from '../common/LeaseStyles';
import { LeaseRejectedAlert } from '../common/LeaseRejectedAlert';

import { leaseCreatorColors } from 'helpers/ColorHelper';
import { getTranslatedString } from 'helpers/i18n';
import LeaseHeaderProgress from '../common/LeaseHeaderProgress';
import { media, sizes } from 'helpers/MediaHelper';

const FORM_NAME = 'lease_document_templates_form';

const StyledFormContainer = styled.div`
  background-color: white;
  width: 100%;
  margin-top: 24px;
  border: 1px solid ${leaseCreatorColors.FORM_BORDER};
  border-radius: 5px;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  font-size: 24px;
  padding: 16px;
  max-width: 580px;
`;

const StyledPageHeader = styled.div`
  font-size: 24px;
  line-height: 32px;
  color: ${leaseCreatorColors.HEADER};
  font-weight: 400;
  margin-bottom: 16px;
`;

const StyledPageSubHeader = styled.div`
  font-size: 18px;
  line-height: 24px;
  color: ${leaseCreatorColors.HEADER};
  font-weight: 400;
  margin-bottom: 16px;
`;

const StyledHeaderDef = styled.div`
  font-size: 14px;
  line-height: 24px;
  color: ${leaseCreatorColors.HEADER};
  font-weight: 400;
  margin-bottom: 16px;
  ${media.mobile`
    font-size: 16px;
  `}
`;

const StyledRow = styled(Row)`
  width: 100%;
  margin-bottom: ${(props) => props.mb || '21px'};
  margin-top: ${(props) => props.mt || '0px'};
`;

const LeaseDocumentTemplatesPage = ({
  forward,
  back,
  goTo,
  currentPage,
  totalPages,
  leaseFormValues,
  setLeaseFormValues,
  leaseDetails,
  handleCancellation,
  saveDraftLease,
  documentTemplatesForProperty,
  fixedAddendumsForLease,
  conditionalAddendumsForLease,
  selectedBaseDocumentTemplate,
  latestLeaseReview,
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [additionalAddendums, setAdditionalAddendums] = useState([]);

  useEffect(() => {
    const documentTemplateObject = (documentTemplate) => {
      if (documentTemplate.signable_doc_type === 'conditional_addendum') {
        return {
          label: documentTemplate.label,
          document_template_id: documentTemplate.value,
          disableDelete: true,
          deleteTooltipText: getTranslatedString(
            'landlordPortal.leaseCreatorV2.leaseDocumentTemplatesPage.tooltipConditionalAddendum'
          ),
        };
      }
      return {
        label: documentTemplate.label,
        document_template_id: documentTemplate.value,
        disableDelete: true,
        deleteTooltipText: getTranslatedString(
          'landlordPortal.leaseCreatorV2.leaseDocumentTemplatesPage.tooltipFixedAddendum'
        ),
      };
    };
    let additionalAddendumsTemp = [];

    const availableAdditionalAddendums = [
      ...fixedAddendumsForLease,
      ...conditionalAddendumsForLease,
    ];
    const availableAdditionalAddendumsIds = availableAdditionalAddendums.map(
      (addendum) => addendum.value
    );
    const savedAdditionalAddendumIds = leaseDetails.draft_lease_data.additional_addendums?.map(
      (addendum) => addendum.document_template_id
    );

    // if there is saved additional addendums and they are same as current additional addendums
    // in store, use saved additional addendums
    // otherwise use only the additional addendums from store
    if (
      !isEmpty(leaseDetails.draft_lease_data.additional_addendums) &&
      isEqual(availableAdditionalAddendumsIds.sort(), savedAdditionalAddendumIds.sort())
    ) {
      additionalAddendumsTemp = [...leaseDetails.draft_lease_data.additional_addendums];
    } else {
      additionalAddendumsTemp = availableAdditionalAddendums.map((availableAddendum) =>
        documentTemplateObject(availableAddendum)
      );
    }
    setAdditionalAddendums(additionalAddendumsTemp);
  }, [
    fixedAddendumsForLease,
    conditionalAddendumsForLease,
    leaseDetails.draft_lease_data.additional_addendums,
  ]);

  const leaseDetailsPage = 1;

  const isMobile = window.matchMedia(`(max-width: ${sizes.mobile}px)`).matches;

  const WarningModal = () => (
    <Modal
      warningModal
      headerContent={getTranslatedString(
        'landlordPortal.leaseCreatorV2.leaseDocumentTemplatesPage.modalHeaderContent'
      )}
      bodyContent={getTranslatedString(
        'landlordPortal.leaseCreatorV2.leaseDocumentTemplatesPage.modalBodyContent'
      )}
      YesButtonContent={getTranslatedString(
        'landlordPortal.leaseCreatorV2.leaseDocumentTemplatesPage.modalYesContent'
      )}
      NoButtonContent={getTranslatedString(
        'landlordPortal.leaseCreatorV2.leaseDocumentTemplatesPage.modalNoContent'
      )}
      isMobile={isMobile}
      YesButtonAction={() => goTo(leaseDetailsPage)}
      NoButtonAction={() => setIsModalVisible(false)}
    />
  );

  return (
    <>
      {isModalVisible && <WarningModal />}
      <StyledContainer>
        <LeaseHeaderProgress
          lease={leaseDetails}
          stepsFilled={currentPage}
          totalSteps={totalPages}
        />

        <Formik
          enableReinitialize
          initialValues={{
            additional_addendums: additionalAddendums || [],
          }}
          onSubmit={(values) => {
            setLeaseFormValues((leaseFormValues) => ({
              ...leaseFormValues,
              ...values,
            }));
            saveDraftLease(values);
            forward();
          }}
        >
          {({ handleSubmit, values, setFieldValue }) => {
            const onDragFinish = (newList) => {
              setFieldValue('additional_addendums', newList);
            };
            return (
              <StyledFormContainer>
                <StyledForm id={FORM_NAME} onSubmit={handleSubmit}>
                  <StyledPageHeader>
                    {getTranslatedString(
                      'landlordPortal.leaseCreatorV2.leaseDocumentTemplatesPage.leaseDocuments'
                    )}
                  </StyledPageHeader>
                  {latestLeaseReview?.status === 'rejected' && (
                    <StyledRow gutter={16}>
                      <LeaseRejectedAlert latestLeaseReview={latestLeaseReview} />
                    </StyledRow>
                  )}
                  <StyledPageSubHeader>
                    {getTranslatedString(
                      'landlordPortal.leaseCreatorV2.leaseDocumentTemplatesPage.baseLease'
                    )}
                  </StyledPageSubHeader>
                  <StyledRow md={16}>
                    <Col md={24}>
                      <Subcontent
                        label={selectedBaseDocumentTemplate.label}
                        collapsed={true}
                        hideDelete={true}
                        onEditClick={() => setIsModalVisible(true)}
                        isMobile={isMobile}
                      />
                    </Col>
                  </StyledRow>

                  {!isEmpty(values.additional_addendums) && (
                    <React.Fragment>
                      <StyledPageSubHeader>
                        {getTranslatedString(
                          'landlordPortal.leaseCreatorV2.leaseDocumentTemplatesPage.addenda'
                        )}
                      </StyledPageSubHeader>
                      <StyledHeaderDef>
                        {getTranslatedString(
                          'landlordPortal.leaseCreatorV2.leaseDocumentTemplatesPage.reorderLeaseAddendumDocuments'
                        )}
                      </StyledHeaderDef>
                      <StyledRow md={16}>
                        <Col md={24}>
                          <DragAndDropSubcontent
                            hideEdit={true}
                            contentList={values.additional_addendums}
                            onDragFinish={onDragFinish}
                            isMobile={isMobile}
                          />
                        </Col>
                      </StyledRow>
                    </React.Fragment>
                  )}
                </StyledForm>
              </StyledFormContainer>
            );
          }}
        </Formik>
        <LeaseCreatorPageFooter
          back={back}
          currentPage={currentPage}
          totalPages={totalPages}
          formName={FORM_NAME}
          handleCancellation={handleCancellation}
        />
      </StyledContainer>
    </>
  );
};

const mapStoreToProps = (store) => ({
  documentTemplatesForProperty: store.landlordApplications.documentTemplatesForProperty,
  selectedBaseDocumentTemplate: store.landlord.leaseCreatorV2.selectedBaseDocumentTemplate,
  fixedAddendumsForLease: store.landlord.leaseCreatorV2.fixedAddendumsForLease,
  conditionalAddendumsForLease: store.landlord.leaseCreatorV2.conditionalAddendumsForLease,
  latestLeaseReview: store.landlord.leaseCreatorV2.latestLeaseReview,
});

const mapDispatchToProps = {};

export default connect(mapStoreToProps, mapDispatchToProps)(LeaseDocumentTemplatesPage);
